import { useCallback, useEffect, useState } from 'react';

/** 계정 저장 길은 필요할 때 불러온다 — 표 컴포넌트가 firebase 를 바로 끌어오면 렌더 점검(web:render)이 깨진다. */
const loadMe = () => import('./me').then((m) => m.loadMe());
const setMyPref = (a: { key: string; value: unknown }) => import('./api').then((m) => m.api.setMyPref(a));

/**
 * 표의 **칼럼 폭·표시 여부·순서**를 기억한다. 화면마다 따로, **계정에**(10-15 way 「항목저장이 계정별로」).
 *
 * 저장은 두 곳: 서버 `user_pref`(키 `cols.<tableId>` · 정본 — 다른 PC 에서도 같다) + 이 브라우저(바로 그리기용 사본).
 * 열 때는 브라우저 사본으로 먼저 그리고 getMe 의 계정 값이 오면 그것으로 바꾼다. 계정에 아직 없고 브라우저에만 있으면
 * (10-15 전에 저장한 것) 그 값을 계정으로 한 번 올린다. 폭 끌기는 연달아 부르므로 서버 쓰기는 0.6초 모아서 한 번.
 *
 * 화면이 **숨긴 칼럼 수를 늘 드러내는** 이유는 그대로다(조용한 숨김 금지).
 * `tableId` 는 **탭까지 구분**해야 한다 — 기준정보처럼 한 화면이 탭마다 다른 칼럼을 쓰면 키가 겹쳐 서로의 설정을 덮어쓴다.
 */
export interface TablePrefs {
  /** 숨긴 칼럼 key. */
  hidden: string[];
  /** 칼럼 key → 픽셀 폭. 사용자가 끈 것만 들어간다. */
  width: Record<string, number>;
  /** 칼럼 key 순서(#146 — 머리글을 끌어서 바꾼 것). 비면 화면이 정한 순서. 새로 생긴 칼럼은 원래 이웃 뒤에 끼운다(`applyOrder`). */
  order: string[];
}

const EMPTY: TablePrefs = { hidden: [], width: {}, order: [] };
const keyOf = (tableId: string) => `businweb.cols.${tableId}`;

/** 폭 하한 — 이보다 좁히면 머리글이 읽히지 않아 되돌릴 수도 없게 된다. */
export const MIN_COL_WIDTH = 48;

/**
 * 폭을 저장한 표(`table-fixed`)에서 폭이 없는 칸의 기본 폭(px).
 * 🔴 비워 두면 저장한 칸들 합이 화면 폭을 다 먹을 때 **새로 생긴 칸이 0 폭으로 눌려 안 보인다**
 *   — 「기본으로」를 눌러야 나타났다(2026-10-22 way · #372 기사명 칸).
 */
export const DEFAULT_COL_WIDTH = 120;
/** `<col>` 의 style — 저장한 폭, 없으면 기본 폭. 폭을 저장한 표(table-fixed)에서만 부른다. */
export const colWidthStyle = (prefs: TablePrefs, key: string) => ({ width: prefs.width[key] ?? DEFAULT_COL_WIDTH });

/** 계정 저장 키 —서버는 `[a-z0-9_.-]{1,60}` 만 받는다(대문자 키는 조용히 안 저장된다 · issues 181). */
const prefKeyOf = (tableId: string) => `cols.${tableId.toLowerCase().replace(/[^a-z0-9_.-]/g, '_')}`.slice(0, 60);
const isEmpty = (p: TablePrefs) => p.hidden.length === 0 && Object.keys(p.width).length === 0 && p.order.length === 0;

function parse(v0: unknown): TablePrefs {
  const v = (v0 && typeof v0 === 'object' ? v0 : {}) as Partial<TablePrefs>;
  return {
    hidden: Array.isArray(v.hidden) ? v.hidden.filter((x) => typeof x === 'string') : [],
    width:
      v.width && typeof v.width === 'object'
        ? Object.fromEntries(Object.entries(v.width).filter(([, w]) => typeof w === 'number' && w >= MIN_COL_WIDTH))
        : {},
    order: Array.isArray(v.order) ? v.order.filter((x) => typeof x === 'string') : [],
  };
}

const pending = new Map<string, ReturnType<typeof setTimeout>>();
/** 계정 설정 하나를 쓴다(0.6초 모아서 · null = 지우기) · 같은 로그인 안에서 새로 여는 화면도 새 값을 보게 getMe 캐시도 고친다. */
function putAccountPref(key: string, value: unknown): void {
  loadMe().then((me) => { me.prefs = { ...(me.prefs ?? {}), [key]: value ?? undefined }; }).catch(() => undefined);
  clearTimeout(pending.get(key));
  pending.set(key, setTimeout(() => {
    pending.delete(key);
    setMyPref({ key, value: value ?? null }).catch(() => undefined);
  }, 600));
}
function writeAccount(tableId: string, prefs: TablePrefs): void {
  putAccountPref(prefKeyOf(tableId), isEmpty(prefs) ? null : prefs);
}

/**
 * 표 하나가 자체로 들고 있는 칸 설정(월정기 칸 순서·폭처럼 `useTablePrefs` 를 안 쓰는 표)을 계정에 기억한다 — 같은 규칙:
 * 브라우저 사본으로 먼저, 계정 값이 오면 그것으로, 계정에 없고 브라우저에만 있으면 한 번 올린다. `parseFn` 이 null 이면 기본값.
 */
export function useAccountPref<T>(prefKey: string, localKey: string, parseFn: (v: unknown) => T | null): [T | null, (v: T | null) => void] {
  const fromLocal = (): T | null => {
    try { return parseFn(JSON.parse(localStorage.getItem(localKey) ?? 'null')); } catch { return null; }
  };
  const [v, setV] = useState<T | null>(fromLocal);
  useEffect(() => {
    let cancelled = false;
    loadMe().then((me) => {
      if (cancelled) return;
      const saved = me.prefs?.[prefKey];
      const acc = saved === undefined || saved === null ? null : parseFn(saved);
      if (acc !== null) {
        setV(acc);
        try { localStorage.setItem(localKey, JSON.stringify(acc)); } catch { /* 사본만 못 남긴다 */ }
      } else {
        const local = fromLocal();
        if (local !== null) putAccountPref(prefKey, local);
      }
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [prefKey, localKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = useCallback((next: T | null) => {
    setV(next);
    try {
      if (next === null) localStorage.removeItem(localKey);
      else localStorage.setItem(localKey, JSON.stringify(next));
    } catch { /* 저장 못 해도 이번 화면에서는 바뀐다 */ }
    putAccountPref(prefKey, next);
  }, [prefKey, localKey]);
  return [v, set];
}

function read(tableId: string): TablePrefs {
  try {
    const raw = localStorage.getItem(keyOf(tableId));
    return raw ? parse(JSON.parse(raw)) : EMPTY;
  } catch {
    // 저장값이 깨졌거나 사생활 보호 모드다. 기본 모양으로 도는 게 맞다 — 표를 못 그리게 하지 않는다.
    return EMPTY;
  }
}

function write(tableId: string, prefs: TablePrefs): void {
  try {
    // 기본 상태면 키를 지운다 — 남겨 두면 "설정한 적 없는데 왜 기억하지"가 된다.
    if (isEmpty(prefs)) {
      localStorage.removeItem(keyOf(tableId));
      return;
    }
    localStorage.setItem(keyOf(tableId), JSON.stringify(prefs));
  } catch {
    /* 기억하지 못해도 표는 그대로 돌아가야 한다 */
  }
}

export function useTablePrefs(tableId: string | undefined): {
  prefs: TablePrefs;
  isHidden: (key: string) => boolean;
  toggleHidden: (key: string) => void;
  setWidth: (key: string, px: number) => void;
  setOrder: (keys: string[]) => void;
  reset: () => void;
  /** 기본값에서 벗어나 있는가 — 화면이 "되돌리기"를 보여줄지 정하는 데 쓴다. */
  customized: boolean;
  /** 저장할 때마다 1씩 는다 — 칼럼 줄이 「항목저장」을 잠깐 띄우는 신호(#215). 0 = 이번 화면에서 아직 안 바꿈. */
  savedTick: number;
  /** 「항목저장」 버튼(#291) — 바꿀 때마다 이미 저장되지만, 레거시처럼 눌러 계정에 한 번 더 확정한다. */
  saveNow: () => void;
} {
  const [prefs, setPrefs] = useState<TablePrefs>(EMPTY);
  const [savedTick, setSavedTick] = useState(0);

  // `tableId` 가 바뀌면(탭 전환) 그 표의 설정을 다시 읽는다 — 브라우저 사본으로 먼저, 계정 값이 오면 그것으로.
  useEffect(() => {
    if (!tableId) { setPrefs(EMPTY); return; }
    const local = read(tableId);
    setPrefs(local);
    let cancelled = false;
    loadMe().then((me) => {
      if (cancelled) return;
      const saved = me.prefs?.[prefKeyOf(tableId)];
      if (saved !== undefined && saved !== null) {
        const acc = parse(saved);
        setPrefs(acc);
        write(tableId, acc);
      } else if (!isEmpty(local)) {
        writeAccount(tableId, local); // 10-15 전에 이 브라우저에만 저장한 것 → 계정으로 한 번 올린다
      }
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [tableId]);

  const save = useCallback(
    (next: TablePrefs) => {
      setPrefs(next);
      if (tableId) { write(tableId, next); writeAccount(tableId, next); }
      setSavedTick((n) => n + 1);
    },
    [tableId],
  );

  const toggleHidden = useCallback(
    (key: string) => {
      const has = prefs.hidden.includes(key);
      save({ ...prefs, hidden: has ? prefs.hidden.filter((k) => k !== key) : [...prefs.hidden, key] });
    },
    [prefs, save],
  );

  const setWidth = useCallback(
    (key: string, px: number) => {
      save({ ...prefs, width: { ...prefs.width, [key]: Math.max(MIN_COL_WIDTH, Math.round(px)) } });
    },
    [prefs, save],
  );

  const setOrder = useCallback((keys: string[]) => save({ ...prefs, order: keys }), [prefs, save]);

  return {
    prefs,
    isHidden: (key) => prefs.hidden.includes(key),
    toggleHidden,
    setWidth,
    setOrder,
    reset: () => save(EMPTY),
    customized: prefs.hidden.length > 0 || Object.keys(prefs.width).length > 0 || prefs.order.length > 0,
    savedTick,
    saveNow: () => save(prefs),
  };
}

/**
 * 저장한 순서대로 칼럼을 늘어놓는다. 저장 뒤 화면에 **새로 생긴 칼럼**은 원래 바로 앞 칼럼 뒤에 끼운다
 * (맨 끝으로 몰면 「그 칸이 어디 갔지」가 된다). 없어진 칼럼 key 는 그냥 건너뛴다.
 */
export function applyOrder<T extends { key: string }>(columns: T[], order: string[]): T[] {
  if (order.length === 0) return columns;
  const byKey = new Map(columns.map((c) => [c.key, c]));
  const out = order.filter((k) => byKey.has(k)).map((k) => byKey.get(k)!);
  columns.forEach((c, i) => {
    if (out.includes(c)) return;
    const prev = columns[i - 1];
    out.splice(prev ? out.indexOf(prev) + 1 : 0, 0, c);
  });
  return out;
}

/** `from` 칼럼을 `to` 칼럼 앞(`after` 면 뒤)으로 옮긴 key 순서. */
export function moveKey(keys: string[], from: string, to: string, after: boolean): string[] {
  if (from === to) return keys;
  const rest = keys.filter((k) => k !== from);
  const at = rest.indexOf(to);
  if (at < 0) return keys;
  rest.splice(after ? at + 1 : at, 0, from);
  return rest;
}
